import React, { useState, useEffect, useMemo } from "react";
import { useTheme, alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Drawer,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  Stack,
  Chip,
  Paper,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AssuredWorkloadOutlinedIcon from "@mui/icons-material/AssuredWorkloadOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import PercentOutlinedIcon from "@mui/icons-material/PercentOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import axios from "axios";
import {
  updateEscandalloSubgrupo,
  getGrupos,
} from "../../api/Escandallo/escandalloApi";
import {
  pageFieldSx,
  pageSectionTitleSx,
} from "../../styles/pageModernStyles";

const API_URL = process.env.REACT_APP_API_URL;

const EMPTY_FORM = {
  GRUPO: "",
  SUBGRUPO: "",
  COMPOSICION: "S",
  DutiesChina: "",
  DutiesIndia: "",
  Coste: "",
};

const parseOptionalNumber = (value) => {
  if (value === "" || value == null) return "";
  const n = parseFloat(value);
  return Number.isNaN(n) ? "" : n;
};

const DrawerDutties = ({ open, onClose, data, onSave }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { t } = useTranslation();
  const accent = theme.palette.primary.main;

  const [formData, setFormData] = useState(EMPTY_FORM);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [grupos, setGrupos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isEdit = Boolean(formData.ID_SUBGRUPO);

  const fieldSx = useMemo(
    () => ({
      ...pageFieldSx(theme),
      width: "100%",
      minWidth: 0,
    }),
    [theme],
  );

  const readOnlyFieldSx = useMemo(
    () => ({
      ...fieldSx,
      "& .MuiInputBase-root": {
        backgroundColor:
          theme.palette.caja?.main ||
          (theme.palette.mode === "dark"
            ? theme.palette.action.selected
            : theme.palette.grey[100]),
      },
      "& .MuiInputBase-input": {
        fontWeight: 600,
      },
    }),
    [theme, fieldSx],
  );

  const sectionPaperSx = useMemo(
    () => ({
      p: { xs: 1.5, sm: 1.75 },
      borderRadius: 2.5,
      border: "1px solid",
      borderColor: theme.palette.divider,
      bgcolor: theme.palette.background.paper,
      boxShadow:
        theme.palette.mode === "dark"
          ? "0 2px 8px rgba(0,0,0,0.24)"
          : "0 2px 8px rgba(15,23,42,0.05)",
    }),
    [theme],
  );

  const drawerHeaderBg =
    theme.palette.mode === "dark"
      ? theme.palette.background.paper
      : "linear-gradient(145deg, #e8f6f8 0%, #f4f0ff 48%, #f5faf7 100%)";

  useEffect(() => {
    const fetchGrupos = async () => {
      try {
        const result = await getGrupos();
        setGrupos(result);
      } catch (error) {
        console.error("Error al obtener grupos:", error);
      }
    };
    fetchGrupos();
  }, []);

  useEffect(() => {
    if (data) {
      setFormData(data);
    } else {
      setFormData(EMPTY_FORM);
    }
  }, [data]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    const token = localStorage.getItem("token");
    const payload = {
      ...formData,
      COMPOSICION: "S",
    };

    setSaving(true);
    try {
      if (data?.ID_SUBGRUPO) {
        await updateEscandalloSubgrupo(data.ID_SUBGRUPO, payload);
      } else {
        await axios.post(`${API_URL}/api/escandallo-subgrupo`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      onClose();
      onSave();
    } catch (err) {
      console.error("Error al guardar:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const token = localStorage.getItem("token");
    setDeleting(true);
    try {
      await axios.delete(
        `${API_URL}/api/escandallo-subgrupo/${formData.ID_SUBGRUPO}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setConfirmDelete(false);
      onSave();
      onClose();
    } catch (err) {
      console.error("Error al eliminar subgrupo:", err);
    } finally {
      setDeleting(false);
    }
  };

  const sectionHeading = (Icon, label) => (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: 1.25,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: alpha(accent, 0.12),
          color: theme.palette.primary.dark,
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: 16 }} />
      </Box>
      <Typography sx={{ ...pageSectionTitleSx, mb: 0 }}>{label}</Typography>
    </Stack>
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{
        sx: {
          "& .MuiBackdrop-root": {
            backgroundColor: alpha(theme.palette.common.black, 0.55),
          },
        },
      }}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 440 },
          maxWidth: "100%",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          borderTopLeftRadius: { xs: 0, sm: 16 },
          borderBottomLeftRadius: { xs: 0, sm: 16 },
          border: { xs: 0, sm: `1px solid ${theme.palette.divider}` },
          boxShadow: `-12px 0 40px ${alpha(theme.palette.common.black, 0.14)}`,
          bgcolor: theme.palette.background.default,
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: { xs: 1.5, sm: 2.5 },
          pt: { xs: 1.5, sm: 2 },
          pb: { xs: 1.5, sm: 1.75 },
          flexShrink: 0,
          borderBottom: `1px solid ${theme.palette.divider}`,
          background: drawerHeaderBg,
          position: "relative",
          zIndex: 2,
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: `linear-gradient(90deg, ${accent}, ${
              theme.palette.secondary?.main || theme.palette.primary.light
            })`,
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={1}
        >
          <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                bgcolor:
                  theme.palette.mode === "dark"
                    ? alpha(accent, 0.22)
                    : "#d4f0f4",
                color: theme.palette.primary.dark,
              }}
            >
              <AssuredWorkloadOutlinedIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="overline"
                sx={{
                  fontWeight: 800,
                  letterSpacing: 0.8,
                  color: "text.secondary",
                  lineHeight: 1.2,
                  display: "block",
                }}
              >
                CRM
              </Typography>
              <Typography
                component="h2"
                variant="h6"
                sx={{
                  fontWeight: 800,
                  letterSpacing: -0.2,
                  lineHeight: 1.25,
                  fontSize: { xs: "1.05rem", sm: "1.1rem" },
                }}
              >
                {isEdit
                  ? t("DuttiesDrawer.editTitle")
                  : t("DuttiesDrawer.newTitle")}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.5, lineHeight: 1.45 }}
              >
                {t("DuttiesDrawer.subtitle", {
                  defaultValue:
                    "Aranceles China / India y coste de escandallo del subgrupo.",
                })}
              </Typography>
              {isEdit && formData.GRUPO ? (
                <Stack
                  direction="row"
                  spacing={0.5}
                  useFlexGap
                  flexWrap="wrap"
                  sx={{ mt: 0.75, rowGap: 0.35 }}
                >
                  <Chip
                    icon={<FolderOutlinedIcon sx={{ fontSize: "14px !important" }} />}
                    label={formData.GRUPO}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: "0.68rem",
                      fontWeight: 700,
                      bgcolor: alpha(theme.palette.warning.main, 0.12),
                      color: theme.palette.warning.dark,
                    }}
                  />
                  {formData.SUBGRUPO ? (
                    <Chip
                      label={formData.SUBGRUPO}
                      size="small"
                      variant="outlined"
                      sx={{ height: 22, fontSize: "0.68rem", fontWeight: 700 }}
                    />
                  ) : null}
                </Stack>
              ) : null}
            </Box>
          </Stack>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label={t("common.close")}
            sx={{
              color: "text.secondary",
              border: 1,
              borderColor: theme.palette.divider,
              bgcolor: theme.palette.background.paper,
              flexShrink: 0,
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      {/* Body */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          px: { xs: 1.5, sm: 2.5 },
          py: { xs: 1.5, sm: 2 },
          WebkitOverflowScrolling: "touch",
          bgcolor: theme.palette.background.default,
        }}
      >
        <Stack spacing={1.5}>
          <Paper variant="outlined" sx={sectionPaperSx}>
            {sectionHeading(
              FolderOutlinedIcon,
              t("DuttiesDrawer.sectionIdentity", {
                defaultValue: "Subgrupo",
              }),
            )}
            <Stack spacing={1.25}>
              {isEdit ? (
                <TextField
                  label={t("DuttiesDrawer.group")}
                  fullWidth
                  size="small"
                  sx={readOnlyFieldSx}
                  value={formData.GRUPO || ""}
                  InputProps={{ readOnly: true }}
                />
              ) : (
                <FormControl fullWidth size="small" sx={fieldSx}>
                  <InputLabel id="grupo-label-dutties">
                    {t("DuttiesDrawer.group")}
                  </InputLabel>
                  <Select
                    labelId="grupo-label-dutties"
                    value={formData.ID_GRUPO || ""}
                    label={t("DuttiesDrawer.group")}
                    onChange={(e) => handleChange("ID_GRUPO", e.target.value)}
                  >
                    {grupos.map((g) => (
                      <MenuItem key={g.ID_GRUPO} value={g.ID_GRUPO}>
                        {g.GRUPO}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <TextField
                label={t("DuttiesDrawer.subgroup")}
                fullWidth
                size="small"
                sx={isEdit ? readOnlyFieldSx : fieldSx}
                value={formData.SUBGRUPO || ""}
                onChange={(e) => handleChange("SUBGRUPO", e.target.value)}
                InputProps={isEdit ? { readOnly: true } : undefined}
              />

              <TextField
                label={t("DuttiesDrawer.composition")}
                fullWidth
                size="small"
                sx={readOnlyFieldSx}
                value="S"
                InputProps={{ readOnly: true }}
              />
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={sectionPaperSx}>
            {sectionHeading(
              PercentOutlinedIcon,
              t("DuttiesDrawer.sectionDuties", { defaultValue: "Aranceles" }),
            )}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
              <TextField
                label={t("DuttiesDrawer.dutiesChina")}
                fullWidth
                size="small"
                type="number"
                sx={fieldSx}
                inputProps={{ step: 0.01, min: 0 }}
                value={formData.DutiesChina ?? ""}
                onChange={(e) =>
                  handleChange("DutiesChina", parseOptionalNumber(e.target.value))
                }
                InputProps={{
                  endAdornment: (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 700, pr: 0.5 }}
                    >
                      %
                    </Typography>
                  ),
                }}
              />
              <TextField
                label={t("DuttiesDrawer.dutiesIndia")}
                fullWidth
                size="small"
                type="number"
                sx={fieldSx}
                inputProps={{ step: 0.01, min: 0 }}
                value={formData.DutiesIndia ?? ""}
                onChange={(e) =>
                  handleChange("DutiesIndia", parseOptionalNumber(e.target.value))
                }
                InputProps={{
                  endAdornment: (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 700, pr: 0.5 }}
                    >
                      %
                    </Typography>
                  ),
                }}
              />
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={sectionPaperSx}>
            {sectionHeading(
              PaymentsOutlinedIcon,
              t("DuttiesDrawer.sectionCost", { defaultValue: "Coste" }),
            )}
            <TextField
              label={t("DuttiesDrawer.coste")}
              fullWidth
              size="small"
              type="number"
              sx={fieldSx}
              inputProps={{ step: 0.01, min: 0 }}
              value={formData.Coste ?? ""}
              onChange={(e) =>
                handleChange("Coste", parseOptionalNumber(e.target.value))
              }
            />
          </Paper>

          {isEdit ? (
            <Button
              variant="text"
              color="error"
              fullWidth
              onClick={() => setConfirmDelete(true)}
              disabled={deleting}
              startIcon={<DeleteOutlineOutlinedIcon />}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                py: 1,
              }}
            >
              {t("DuttiesDrawer.delete")}
            </Button>
          ) : null}
        </Stack>
      </Box>

      {/* Footer fijo */}
      <Box
        sx={{
          px: { xs: 1.5, sm: 2.5 },
          py: { xs: 1.5, sm: 2 },
          flexShrink: 0,
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.background.paper,
          pb: { xs: "max(1.5rem, env(safe-area-inset-bottom))", sm: 2 },
        }}
      >
        <Stack direction="row" spacing={1.25}>
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={saving}
            sx={{
              flex: 1,
              py: 1.1,
              fontWeight: 700,
              textTransform: "none",
              borderRadius: 2,
              borderColor: alpha(theme.palette.text.primary, 0.18),
            }}
          >
            {t("DuttiesDrawer.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{
              flex: 1,
              py: 1.1,
              fontWeight: 800,
              textTransform: "none",
              borderRadius: 2,
              color: "#fff",
              bgcolor: accent,
              boxShadow: `0 6px 18px ${alpha(accent, 0.35)}`,
              "&:hover": {
                bgcolor: theme.palette.primary.dark,
                boxShadow: `0 8px 22px ${alpha(accent, 0.42)}`,
              },
            }}
            startIcon={
              saving ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            {t("DuttiesDrawer.save")}
          </Button>
        </Stack>
      </Box>

      <Dialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 3,
            minWidth: 300,
            m: { xs: 0, sm: 2 },
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          {t("DuttiesDrawer.deleteTitle")}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t("DuttiesDrawer.deleteBody")}
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            px: 2.5,
            pb: 2,
            pt: 0,
            gap: 1,
            flexDirection: { xs: "column", sm: "row" },
          }}
        >
          <Button
            onClick={() => setConfirmDelete(false)}
            variant="outlined"
            fullWidth
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {t("DuttiesDrawer.cancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={deleting}
            fullWidth
            startIcon={
              deleting ? <CircularProgress size={16} color="inherit" /> : null
            }
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {t("DuttiesDrawer.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
};

export default DrawerDutties;
